import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type DaySummary, type Task } from '@/api/client'
import { useAuth } from '@/lib/auth'
import { type GroupBy } from '@/components/TaskTable'
import { TaskList } from '@/components/TaskList'
import { TaskDrawer } from '@/components/TaskDrawer'
import { AddTaskDrawer } from '@/components/AddTaskDrawer'
import { SignOffModal, SignedOffBanner, SignOffTag }
  from '@/components/SignOffDay'
import { ChasePanel } from '@/components/ChasePanel'
import { DropSelected } from '@/components/DropSelected'
import { useSelection } from '@/components/Selection'
import { useChannel } from '@/lib/channel'
import { Banner, DayBar, Empty, ErrorNote, Spinner, addDays, fmtDate, todayISO }
  from '@/components/ui'
import { Link } from 'react-router-dom'
import { type Incident } from '@/api/client'

const FILTER_TO_STATUS: Record<string, string> = {
  completed: 'COMPLETED', pending_approval: 'PENDING_APPROVAL',
  rework: 'REWORK', open: 'OPEN',
}

export function Today() {
  const { can } = useAuth()
  // Capability only. `can('cancel')` is what `/auth/me` publishes; every task is
  // re-checked server-side, so a manager reaching past their own team is refused
  // there and reported, not silently filtered here.
  const dropping = can('cancel')
  const picked = useSelection()
  const { label: channelName } = useChannel()
  const qc = useQueryClient()
  // State, not a constant. It was `todayISO()` and nothing else, which made this
  // screen structurally incapable of showing another day - so the one thing the Owner
  // asked for after signing off, getting ahead on tomorrow, had no route to it.
  // Completing a future-dated task has always worked; there was simply nowhere to see
  // one.
  const [day, setDay] = useState(todayISO())
  const [open, setOpen] = useState<Task | null>(null)
  const [adding, setAdding] = useState(false)
  const [chasing, setChasing] = useState(false)
  const [filter, setFilter] = useState<string | null>(null)
  // A tick must not survive into a list that no longer shows it. Filter down to
  // REWORK after ticking three OPEN tasks and "Drop 3" would drop three rows
  // nobody could see - which is the one way a bulk action becomes dangerous.
  useEffect(() => { picked.clear() }, [filter, day, picked.clear])
  const [groupBy, setGroupBy] = useState<GroupBy>('company')
  const [err, setErr] = useState<unknown>(null)
  const [signingOff, setSigningOff] = useState(false)

  const summary = useQuery({
    queryKey: ['day', day],
    queryFn: () => api.get<DaySummary>(`/days/${day}`),
  })
  const tasks = useQuery({
    queryKey: ['tasks', { date: day }],
    queryFn: () => api.get<Task[]>(`/tasks?date=${day}`),
  })

  // The "visible on sign-in" half of alerting. Every session lands here, so an
  // urgent problem that has not been acknowledged says so at the top of the page -
  // and it says so whether or not the Telegram message ever got out, which is the
  // case the screen exists for. Superuser only: it is their alert stream.
  const { me } = useAuth()
  const incidents = useQuery({
    queryKey: ['incidents-open'],
    queryFn: () => api.get<Incident[]>('/ops/incidents?open_only=true'),
    enabled: !!me?.is_superuser,
    refetchInterval: 60_000,
  })
  const urgent = (incidents.data ?? []).filter((x) => x.severity === 'immediate')

  // This viewer's own sign-off row, if there is one. The summary already carries the
  // scoped list, so nothing here costs a second request on the busiest screen.
  const closes = summary.data?.closes ?? []
  const mine = closes.find((r) => r.mine)
  // Everybody else the viewer can see who is currently signed off. Reopened rows are
  // in `closes` too - useful on the Owner's own screen, noise here.
  const team = closes.filter((r) => !r.mine && r.status === 'CLOSED')
  // What THIS viewer still has on this day. The day's counters are the whole visible
  // list, which for an Owner is the firm's - a different number entirely, and the one
  // the sign-off dialog must not quote.
  const ownOutstanding = useMemo(() => {
    const rows = tasks.data ?? []
    const uid = me?.user_id
    const doing = rows.filter(
      (t) => t.people?.some((p) => p.kind === 'RESPONSIBLE' && p.user_id === uid))
    return {
      open: doing.filter((t) => t.status === 'OPEN' || t.status === 'REWORK').length,
      awaiting: rows.filter(
        (t) => t.status === 'PENDING_APPROVAL'
          && t.people?.some((p) => p.kind === 'APPROVER' && p.user_id === uid
                            && !p.decision)).length,
    }
  }, [tasks.data, me?.user_id])
  const dismiss = useMutation({
    // Dismissing IS acknowledging - a banner that comes back tomorrow after being
    // closed is how a warning becomes wallpaper.
    mutationFn: () => api.post('/ops/incidents/ack',
                               { alert_ids: urgent.map((x) => x.alert_id) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['incidents-open'] })
      qc.invalidateQueries({ queryKey: ['incident-count'] })
    },
  })

  const [digestNote, setDigestNote] = useState<string | null>(null)
  const digest = useMutation({
    mutationFn: () => api.post<{ result?: Record<string, unknown> }>(
      '/ops/jobs/send_digest/run'),
    onSuccess: (out) => {
      setErr(null)
      // The job is idempotent, so a second press is refused with a reason - and
      // throwing that reason away is what made the button look broken. Pressing it
      // again did nothing, said nothing, and the Owner reasonably concluded it had
      // only worked once.
      const sent = out?.result as Record<string, unknown> | undefined
      setDigestNote(
        sent && sent.skipped === 'already sent'
          ? "Today's list has already gone out. Anything you add now is sent on its "
            + 'own within a couple of minutes — you do not need this button again.'
          : sent && sent.skipped
            ? String(sent.skipped)
            : 'Sent.')
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['day'] })
    },
    onError: setErr,
  })

  const shown = useMemo(() => {
    const all = tasks.data ?? []
    if (!filter) return all
    return all.filter((t) => t.status === FILTER_TO_STATUS[filter])
  }, [tasks.data, filter])

  // Grouping by person lists a shared task under each of its doers, so the headings
  // add up to more than the day's total. That is the honest answer to "what is on
  // this person" - but only the footer can say so, and only when it is true.
  const shared = useMemo(
    () => shown.filter(
      (t) => (t.people ?? []).filter((p) => p.kind === 'RESPONSIBLE').length > 1).length,
    [shown])

  if (summary.isLoading || tasks.isLoading) return <Spinner label="Loading today" />
  if (tasks.error) return <ErrorNote error={tasks.error} />

  const s = summary.data
  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      {urgent.length > 0 ? (
        <Banner tone="bad">
          <b>{urgent[0].summary}</b>{' '}
          {urgent.length > 1 ? `and ${urgent.length - 1} other problem${
            urgent.length > 2 ? 's' : ''}. ` : ''}
          <Link to="/system" className="underline">See what happened</Link>
          {' · '}
          <button className="underline" onClick={() => dismiss.mutate()}>
            Dismiss
          </button>
        </Banner>
      ) : null}
      {digestNote ? (
        <Banner tone="info">{digestNote}</Banner>
      ) : null}
      {s?.is_closed && (
        <Banner tone="info">
          {/* The FIRM's day, which is a different thing from signing off yourself -
              this one freezes the record. Kept visually distinct from the sign-off
              banner below, or two different "closed" states read as one. */}
          <b>The firm's day is closed.</b> Tasks dated today cannot be changed until an
          Owner reopens it — that is what keeps the closed record auditable.
        </Banner>
      )}
      {mine?.status === 'CLOSED' && <SignedOffBanner row={mine} day={day} />}
      {s && !s.is_working_day && (
        <Banner tone="warn">
          Today is not a working day in your calendar, so no digest or carry-forward
          will run.
        </Banner>
      )}
      {s?.unassigned?.length ? (
        <Banner tone="warn">
          <b>{s.unassigned.length} row(s) have no responsible person</b> — nobody was
          notified about them. Refs {s.unassigned.join(', ')}.
        </Banner>
      ) : null}

      {s && (
        <DayBar
          counters={s.counters}
          title={day === todayISO() ? "Today's list"
                 : day === addDays(todayISO(), 1) ? "Tomorrow's list"
                 : fmtDate(day)}
          active={filter}
          onFilter={setFilter}
          note={
            (
              <span className="flex flex-wrap gap-2 items-center">
                {/* Getting ahead. Completing a future-dated task has always worked -
                    nothing anywhere requires a task to be acted on its own date - and
                    there was simply no way to SEE one from here. Two arrows are the
                    whole of that request. */}
                <span className="inline-flex rounded-md border border-rule overflow-hidden">
                  <button className="px-2 h-7 text-[12px] hover:bg-paper"
                          aria-label="The day before"
                          onClick={() => setDay(addDays(day, -1))}>‹</button>
                  {day !== todayISO() && (
                    <button className="px-2 h-7 text-[12px] border-l border-rule
                                       hover:bg-paper"
                            onClick={() => setDay(todayISO())}>Today</button>
                  )}
                  <button className="px-2 h-7 text-[12px] border-l border-rule
                                     hover:bg-paper"
                          aria-label="The day after"
                          onClick={() => setDay(addDays(day, 1))}>›</button>
                </span>
                {/* OUTSIDE `can('create')`, deliberately: that means "may raise
                    work", and everybody signs off their own day. Gating it there
                    would hide the feature from most of the firm. */}
                {!mine || mine.status === 'OPEN' ? (
                  <button className="btn btn-sm" onClick={() => setSigningOff(true)}>
                    Sign off for the day
                  </button>
                ) : null}
                {/* Owner only, because both of these are firm-wide acts on
                    `owner_only` endpoints - sending the digest to everybody, and
                    chasing the whole day's outstanding work. They sat behind
                    `can('create')`, which used to mean "is the Owner" and now means
                    "may raise work", so a manager saw two buttons that answered 403.
                    Add task keeps `can('create')`: that one is the point. */}
                {me?.is_owner && (
                  <>
                    <button className="btn btn-sm" disabled={digest.isPending}
                            onClick={() => { setDigestNote(null); digest.mutate() }}>
                      {digest.isPending ? 'Sending…' : 'Send digest now'}
                    </button>
                    <button className="btn btn-sm" onClick={() => setChasing(true)}>
                      Chase outstanding
                    </button>
                  </>
                )}
                {can('create') && (
                  <button className="btn btn-sm btn-primary"
                          onClick={() => setAdding(true)}>
                    Add task
                  </button>
                )}
              </span>
            )
          }
        />
      )}

      {/* Who else has signed off. Above the list rather than as a tag on each person
          heading, for two reasons: it is visible whichever way the list is grouped,
          and somebody who has signed off with nothing left may have no rows at all -
          a tag would have nowhere to hang. */}
      {team.length > 0 && (
        <div className="card px-4 py-3 mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-2">
          <b className="text-[13px]">Signed off</b>
          {team.map((r) => (
            <span key={r.user_id} className="inline-flex items-center gap-1.5">
              <span className="text-[12.5px]">{r.name}</span>
              <SignOffTag row={r} />
              {r.outstanding.total > 0 && (
                <span className="mono text-[11px] text-ink-3">
                  {r.outstanding.total} left
                </span>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Group by. Two headings over the same rows rather than a second screen: the
          day is still the unit, and "what is Shyam carrying" and "what is on this
          client" are the two ways this list gets read.

          Hidden when the day is empty - a control over nothing invites a click that
          changes nothing. */}
      {shown.length > 0 && (
        <div className="flex items-center justify-end gap-2 mb-2">
          <span className="text-[11.5px] text-ink-3">Group by</span>
          <div className="inline-flex rounded-md border border-rule overflow-hidden">
            {(['company', 'person'] as const).map((g) => (
              <button key={g} onClick={() => setGroupBy(g)}
                      aria-pressed={groupBy === g}
                      className={`px-2.5 h-7 text-[12px] capitalize transition-colors ${
                        groupBy === g ? 'bg-primary text-primary-on font-medium'
                                      : 'bg-surface text-ink-2 hover:text-ink'}`}>
                {g}
              </button>
            ))}
          </div>
        </div>
      )}

      {shown.length === 0 ? (
        <Empty
          title={filter ? 'Nothing in that state' : 'No tasks for today yet'}
          hint={filter
            ? 'Clear the filter by clicking the same segment again.'
            : 'Recurring rules add their tasks at 06:00, before the morning digest goes out. Anything else is written here.'}
          action={!filter && can('create') ? (
            <span className="flex flex-wrap gap-2 justify-center">
              {/* A Link, not an `<a href>`: a full page load here throws away the
                  in-memory access token and lands the owner back on the sign-in
                  screen. On a phone `/new` says so and offers this same sheet. */}
              <Link className="btn btn-primary" to="/new">Write the day's list</Link>
              <button className="btn" onClick={() => setAdding(true)}>Add one task</button>
            </span>
          ) : undefined}
        />
      ) : (
        <>
        <TaskList
          tasks={shown}
          onOpen={setOpen}
          groupBy={groupBy}
          selection={dropping ? picked : undefined}
          footer={[
            filter
              ? `Filtered to ${shown.length} of ${tasks.data?.length ?? 0}. Click the bar segment again to clear.`
              : `${shown.length} task(s) across ${s?.companies.length ?? 0} company/companies.`,
            // Said only when it is actually true, because the headings really do add
            // up to more than the total and a reader is entitled to notice.
            groupBy === 'person' && shared > 0
              ? `${shared} shared task(s) appear under each person.`
              : '',
          ].filter(Boolean).join(' ')}
        />
        {dropping && <DropSelected selection={picked} channelName={channelName} />}
        </>
      )}

      {open && (
        <TaskDrawer
          task={(tasks.data ?? []).find((t) => t.task_id === open.task_id) ?? open}
          onClose={() => setOpen(null)}
        />
      )}

      {chasing && <ChasePanel onClose={() => setChasing(false)} />}
      {adding && <AddTaskDrawer date={day} onClose={() => setAdding(false)} />}
      {signingOff && s && (
        <SignOffModal
          day={day}
          // THEIR OWN outstanding work, not the day's. `s.counters` is the whole
          // visible day, which for an Owner is the firm - so quoting it here would
          // tell them they were leaving forty tasks behind.
          outstanding={ownOutstanding}
          willTell={ownOutstanding.open + ownOutstanding.awaiting > 0}
          onClose={() => setSigningOff(false)} />
      )}
    </>
  )
}
